<template>
  <div class="p-6 dark:text-gray-50" v-if="(canAddProbe || user?.is_staff)">
    <div class="flex justify-between items-center mb-6">
      <div class="flex space-x-3 items-center">
        <h1 class="text-2xl font-semibold dark:text-white">Add New Probe</h1>
        <div class="relative group">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="startTutorial"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
      </div>
      <button v-if="!isMultiSubmit"
          @click="goBack"
          class="btn bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Back to Probes
      </button>
    </div>

    <form @submit.prevent="submitForm" class="space-y-6 glass-card p-6">
      <!-- Required Fields -->
      <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <h2 class="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Required Information</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div v-for="field in requiredprobeFields" :key="field.key" class="flex flex-col">
            <label :for="field.key" class="mb-1 font-medium text-gray-700 dark:text-gray-200">
              {{ field.label }}
              <span v-if="field.required" class="text-red-500">*</span>
            </label>
            <input
              v-if="field.type === 'text' || field.type === 'number'"
              :id="field.key"
              v-model="probeData[field.key]"
              :type="field.type"
              @focus="clearFieldError(field.key)"
              :placeholder="field.placeholder"
              :class="[
                fieldErrors[field.key]
                  ? 'rounded px-3 py-2 ring-2 ring-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 mb-2'
                  : 'rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2'
              ]"
            />
              
            <GlassSelect
              v-else-if="field.type === 'boolean'"
              :id="field.key"
              v-model="probeData[field.key]"
              :options="[{id:true,name:'Yes'},{id:false,name:'No'}]"
            />

            <GlassSelect
              v-else-if="field.key === 'span_interface'"
              :id="field.key"
              v-model="probeData[field.key]"
              :options="spanInterfaceOptions"
              placeholder="Select an interface"
            />
            
            <div v-else-if="field.type === 'searchableSelect'" class="relative">
              <div class="relative">
                <input 
                  :id="field.key"
                  v-model="siteSearch"
                    :placeholder="getSelectedSiteName || 'Search Site...'"
                  :class="[ 'w-full', fieldErrors[field.key]
                      ? 'border border-red-300 rounded px-3 py-2 ring-2 ring-red-400 focus:outline-none focus:ring-2 focus:ring-red-400 mb-3'
                      : 'border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3'
                  ]"
                  @focus="handleSiteFocus"
                  @blur="handleSiteBlur" 
                  :disabled="isMultiSubmit"
                >
                <div
                  v-show="isSiteDropdownVisible" 
                  class="absolute w-full mt-1 max-h-60 overflow-y-auto bg-white border border-gray-300 rounded shadow-lg z-10"
                  >
                  <div 
                    v-for="site in filteredSites"
                    :key="site.id"
                    class="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:bg-gray-600 dark:text-gray-50 dark:hover:text-gray-900"
                    @mousedown="selectSite(site)"
                  >
                    {{ site.name }}
                  </div>
                  <div 
                    v-if="filteredSites.length === 0" 
                    class="px-3 py-2 text-gray-500"
                  >
                    No Sites found
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="fieldErrors[field.key]"
              class="text-red-500 text-sm mb-3 flex items-center space-x-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-5 h-5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="m4.9 4.9 14.2 14.2" />
              </svg>

              <p class="text-sm font-medium">
                {{ fieldErrors[field.key] }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Optional Fields -->
      <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <h2 class="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Optional Information</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
           <div v-for="field in optionalFields" :key="field.key" class="flex flex-col">
            <label :for="field.key" class="mb-1 font-medium text-gray-700 dark:text-gray-200">
            {{ field.label }}
            <span v-if="field.required" class="text-red-500">*</span>
          </label>
          <input
            v-if="field.type === 'text' || field.type === 'number'"
            :id="field.key"
            v-model="probeData[field.key]"
            :type="field.type"
            :required="field.required"
            :placeholder="field.placeholder"
            :class="[
              fieldErrors[field.key]
                ? 'border border-red-300 rounded px-3 py-2 ring-2 ring-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 mb-2'
                : 'border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2'
            ]"
          />
          
          <!-- Show field-specific error -->
          <div
            v-if="fieldErrors[field.key]"
            class="text-red-500 text-sm mb-3 flex items-center space-x-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="m4.9 4.9 14.2 14.2" />
            </svg>

            <p class="text-sm font-medium">
              {{ fieldErrors[field.key] }}
            </p>
          </div>

          <div v-if="field.key === 'snmp_targets'">
            <button
              type="button"
              :class="{
                'bg-gray-300 hover:bg-gray-400 focus:ring-gray-200': probeData[field.key] === null,
                'bg-green-600 hover:bg-green-700 focus:ring-green-500': probeData[field.key] !== null,
                'px-3 py-1 text-white rounded focus:outline-none focus:ring-2 transition duration-200': true                     
              }"
              class="text-white rounded focus:outline-none transition duration-200 mb-2 dark:bg-blue-600 dark:text-gray-50"
              @click="addTarget(probeData[field.key])"
              >Save Target
            </button>
            <div class="grid grid-cols-4 gap-1 flex-1">
              <div 
                v-for="(target,index) in targets"
                class="flex justify-between bg-gray-300 dark:bg-gray-100 dark:text-gray-900 px-2 py-1 rounded mb-2"
                >
                  <p v-show="targetAdded">{{ target }}</p>
                  <button 
                    type="button"
                    @click="removeTarget(index)"
                    class="text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
            </div>
          </div>

          <div v-if="field.key == 'vlans'">
            <div
              v-for="(vlan, index) in dropdowns" 
              class="bg-white dark:bg-gray-800 p-6 p-6 rounded-lg shadow-sm border border-gray-200 mb-2">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

                  <!-- NAME -->
              <div class="flex flex-col">
                <label for="description" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Name<span class="text-red-500">*</span>:</label>
                <input 
                  v-model="vlan.name"
                  id="name"
                  required
                  placeholder="e.g Non-Payment"
                  class="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <!-- NET INTERFACE  -->
              <div class="flex flex-col">
                <label for="region" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Network interface:<span class="text-red-500">*</span>:</label>
                <GlassSelect
                  v-model="vlan.raw_device"
                  id="raw_device"
                  :options="netfields"
                  placeholder="Select network interface"
                />
              </div>
                      
              <!-- ADDRESS CIDR notation -->
              <div class="flex flex-col">
                <label for="description" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Address CIDR notation<span class="text-red-500">*</span>:</label>
                <input 
                  v-model="vlan.ip_address"
                  id="ip_address"
                  required 
                  placeholder="e.g 10.135.128.254/24"
                  class="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
                  
                  <!-- Gateway: -->
              <div class="flex flex-col">
                <label for="description" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Gateway:<span class="text-red-500">*</span>:</label>
                <input 
                  v-model="vlan.gateway"
                  id="gateway"
                  required 
                  placeholder="e.g 10.135.128.225"
                  class="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
                  
                  <!-- Routes -->
              <div class="flex flex-col rounded"
                v-for="(route, index) in vlan.routes">
                <label for="description" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Routes:</label>
                <label for="description" class="mb-1 font-medium text-gray-700 dark:text-gray-50">to:</label>
                <input 
                  v-model="route.to"
                  id="to"
                  placeholder="e.g 192.168.0.1"
                  class="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <label for="description" class="mb-1 font-medium text-gray-700 dark:text-gray-50">via:</label>
                <input 
                  v-model="route.via"
                  id="via"
                  placeholder="e.g 192.168.0.2"
                  class="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div 
                  v-show="routeAdded"
                  class="flex flex-wrap gap-2 mt-2"
                  >
                  <div
                    v-for="(item, i) in routeArray" :key="i"
                    class="group inline-flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200">
                    <p>{{ item.to }}</p>
                    <button 
                        type="button"
                        @click="removeRoute(item)" 
                        class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 focus:outline-none transition-colors duration-200"
                      >
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        class="h-5 w-5" 
                        viewBox="0 0 20 20" 
                        fill="currentColor">
                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div class="space-x-1">
                  <button
                    type="button"
                    @click.stop="addRoute({to: route.to, via: route.via})"
                    @mousedown.prevent
                    class="mt-2 px-3 py-1 bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                    Add Route
                  </button>
                </div>
              </div>
              
              <div class="m-auto">
                <button
                  type="button"
                  @click="handleSaveVlan(index)"
                  class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                  Save
                </button>
                <button
                  type="button"
                  @click="removeVlan(index)"
                  class="btn ml-2 bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <template 
            v-if="field.key == 'subnets'"
            v-for="subnet in subnets"
          >
          <form @submit.prevent="handleSaveSubnet(subnet)">
            <div class="space-x-2 bg-white dark:bg-gray-800 dark:text-gray-50 p-6 rounded-lg shadow-sm border border-gray-200 mb-2">
              <label for="description" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Subnet CIDR notation<span class="text-red-500">*</span>:</label>
              <input 
                type="text"
                v-model="subnet.ip_address"
                id="ip_address"
                class="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="11.82.14.0/24"
                >
                <div class="m-auto mt-2">
                  <button
                    type="button"
                    @click="handleSaveSubnet(subnet)"
                    class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                    Save
                  </button>
                  <button
                    type="button"
                    @click="removeSubnet(subnet)"
                    class="btn ml-2 bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
          </form>
        </template>

          <GlassSelect
            v-else-if="field.type === 'boolean'"
            :id="field.key"
            v-model="probeData[field.key]"
            :options="[{id:true,name:'Yes'},{id:false,name:'No'}]"
          />
          <GlassSelect
            v-else-if="field.key === 'span_interface'"
            :id="field.key"
            v-model="probeData[field.key]"
            :options="spanInterfaceOptions"
            placeholder="Select an interface"
          /> 
        </div>
        </div>

       
        <div>
          <div 
            v-if="subnetSaved"
            class="flex flex-wrap gap-2 mt-2"
            >
            <div
              v-for="(item, index) in addedSubnets" 
              :key="index"
              class="group inline-flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
            >
              <p>
                {{ item.ip_address }}
              </p>
              <button 
                type="button"
                @click="removeSubnet(index)" 
                class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 focus:outline-none transition-colors duration-200"
                >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  class="h-5 w-5" 
                  viewBox="0 0 20 20" 
                  fill="currentColor"
                  >
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
          <button v-if="canAddSubnet || user?.is_staff"
              type="button"
              @click="handleAddSubnet()"
              class="btn m-2 bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add Subnets
          </button>

          <div 
            v-if="vlanSaved"
            class="flex flex-wrap gap-2 mt-2"
            >
            <div
              v-for="(item, index) in dropdowndata" :key="index"
              class="group inline-flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200">
                <p>{{ item.name }}</p>
                <button 
                  type="button"
                  @click="removeVlan(index)" 
                   class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 focus:outline-none transition-colors duration-200"
                  >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>
            </div>
          </div>
          <button v-if="canAddVLAN || user?.is_staff"
              @click="handleAddVlan()"
              type="button"
              class="btn m-2 bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add VLANs
          </button>
        </div>
      </div>
    
      <div class="flex justify-end space-x-2">
        <button
          type="button"
          @click="$router.push({ name: 'Probes', query: this.tenant.addSchemaParam() })"
          class="btn mr-2 bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 mb-2"
        >
          Cancel
        </button>
        <button
          id="submit-probe-btn"
          type="submit"
          class="btn mr-2 bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
        >
          Add Probe
        </button>
      </div>
      
    </form>
  </div>

  
  <!-- Error Message -->
  <PageError v-if="permsLoaded && !canAddProbe && !user?.is_staff"
    :error="errorMessages['403']"
    :errorCode="'403'"
  />
</template>

<script>
import { createProbe, getAllProbes } from "@/services/probeService";
import { getSites } from "@/services/siteService";
import { createVlan } from "@/services/vlanService";
import { createSubnet } from "@/services/subnetService";
import { useStore } from "@/store";
import { errorMessages } from "@/utils/errorMessages";
import PageError from "@/components/PageError.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import GlassSelect from "@/components/GlassSelect.vue";
import { useTutorial } from "@/composables/tutorial";

export default {
  emits: ['probe-submitted'],
  props: {
    isMultiSubmit: {
      type: Boolean,
      default: false
    },
    newmultiSubmitSite: {
      type: Number
    },
    submittedSite: { 
      type: Object, 
      default: null 
    },
  },
  components: {
    PageError,
    GlassSelect
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      errorMessages: errorMessages,
      addProbeTutorialSteps: [
        {
          element: '#site',
          action: 'display',
          message: 'Search and select the site this probe belongs to.'
        },
        {
          element: '#device',
          action: 'type',
          value: 'router-01',
          message: 'Enter the device identifier for this probe.'
        },
        {
          element: '#dns_one',
          action: 'type',
          value: '8.8.8.8',
          message: 'Enter the primary DNS server address.'
        },
        {
          element: '#ntp',
          action: 'type',
          value: '0.0.0.0',
          message: 'Enter the NTP server address.'
        },
        {
          element: '#submit-probe-btn',
          action: 'display',
          message: 'Click to create the probe.'
        },
      ],
      netfields : ['eth0', 'eth1', 'eth2', 'eth3', 'eth4'],
      errors: [],
      newSubnet : {
        ip_address: null,
        probe: null,
      },
      newVLAN:{
        name: "",
        vlan_id: 1,
        gateway: null,
        ip_address: null,
        routes: [
          { to: "" ,
            via: "",
          }
        ],
        raw_device: "",
        probe: null
      },
      probeData: {
        device: "",
        dns_one: "",
        dns_two: "",
        dns_three: "",
        ntp: "",
        snmp_targets: null,
        api_key: "",
        api_port: null,
        api_address: "",
        span_interface: "",
        is_wifi_probe: false,
        site: null,
      },
      siteSearch: "",
      isSiteDropdownVisible: false,
      sites: [],
      requiredprobeFields: [
        { key: "site", label: "Site", type: "searchableSelect", required: true },
        { key: "device", label: "Device", type: "text", required: true, placeholder: "e.g., router-01" },
        { key: "dns_one", label: "DNS Primary", type: "text", required: true, placeholder: "0.0.0.0" },
        { key: "ntp", label: "NTP", type: "text", required: true, placeholder: "0.0.0.0" },       
      ],
      optionalFields: [
        { key: "dns_two", label: "DNS Secondary", type: "text", required: false, placeholder: "0.0.0.0" },
        { key: "dns_three", label: "DNS Tertiary", type: "text", required: false, placeholder: "0.0.0.0" },
        
        {
          key: "snmp_targets",
          label: "SNMP Target IP addresses",
          type: "text",
          required: false,
          placeholder: "Enter an IP address",
        },
        { key: "api_key", label: "API Key", type: "text", required: false },
        { key: "api_port", label: "API Port", type: "number", required: false },
        { key: "api_address", label: "API Address", type: "text", required: false },
        {
          key: "span_interface",
          label: "Subnet SPAN interface",
          type: "select",
          required: false,
        },
        {
          key: "is_wifi_probe",
          label: "WiFi Probe",
          type: "boolean",
          required: false,
        },
        { key: "subnets", label: "Subnet", type: "subnet", required: false},
        { key: "vlans", label: "VLANs", type: "vlan", required: false},
       
      ],
      spanInterfaceOptions: ["eth0", "eth1", "eth2", "eth3"],
      dropdown: false,           
      dropdowns: [],
      dropdowndata: [],
      vlanSaved: false,
      subnets:[],
      addedSubnets:[],
      subnetSaved: false,
      routeArray: [],
      routeAdded: false,
      targets : [],
      targetAdded: false,
      canAddSubnet: false,
      canAddVLAN: false,
      canAddProbe: false,
      fieldErrors: {},
      multiSubmitSite: ""
    };
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
          const schemaName = this.tenant.currentSchemaName.value;
          this.fetchSites(schemaName);
        }
      },
      immediate: true 
    },
  },
  methods: {
    clearFieldError(fieldName){
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
        delete this.fieldErrors['non_field_errors'];
      }
    },
    async startTutorial() {
      const originalData = { ...this.probeData };
      const originalSiteSearch = this.siteSearch;
      await this._start(this.addProbeTutorialSteps, () => {
        this.probeData = { ...originalData };
        this.siteSearch = originalSiteSearch;
      });
    },
    async checkPermissions() {
      if (this.user) {
        this.canAddSubnet = await useStore.hasPermission("Can add subnet");
        this.canAddVLAN = await useStore.hasPermission("Can add VLAN");
        this.canAddProbe = await useStore.hasPermission("Can add probe");
      }
    },
    goBack(){
      this.$router.push({ 
        name: "Probes",
        query: this.tenant.addSchemaParam()
      });
    },
    async fetchSites(schemaName = null) {
      try {

        // If in multi-submit mode, auto-select populate with submitted site and skip fetching all sites
        if (this.isMultiSubmit && this.submittedSite) {
          this.sites = [this.submittedSite];
          this.selectSite(this.submittedSite);
          return;
        }

        this.sites = await getSites(schemaName);

      } catch (error) {
        console.error("Error fetching sites:", error);
      }
    },
    async submitForm() {
      try {

        const validation = this.tenant.validateSchemaForOperation()
        if (!validation.valid) {
          this.error = "Malformed request. Failed to create probe!";
          this.errorCode = "422!";
          return;
        }
        // Clear the errors
        this.fieldErrors = {}

        // Generate automatic name based on device and site
        const siteName = this.sites[this.probeData.site] || "";
        const timestamp = new Date().getTime();
        const generatedName = `${this.probeData.device}_${siteName}_${timestamp}`.toLowerCase().replace(/\s+/g, "_");

        // Prepare the data for submission
        const probeDataToSubmit = {
          ...this.probeData,
          name: generatedName,
          // Add default values for required backend fields
          is_approved: true,
          is_installed: true,
          in_maintenance: false,
          approvedprobe: false,
          pending: true,
        };

        // Manage Targets
        probeDataToSubmit.snmp_targets = this.targets;

        // Remove any empty optional fields
        const cleanedProbeData = Object.fromEntries(
          Object.entries(probeDataToSubmit).filter(
            ([_, v]) => v !== null && v !== "" && v !== undefined
          )
        );
        
        // Create a probe
        const newProbe = await createProbe(cleanedProbeData, validation.schemaName);
    
        this.probeData.snmp_targets = []
        
        if (newProbe && this.dropdowndata){
          await this.submitVlan(newProbe.id, validation.schemaName);
        }

        if (newProbe && this.newSubnet){
          await this.submitSubnet(newProbe.id, validation.schemaName);
        }

        if (this.isMultiSubmit){
          this.$emit('probe-submitted', newProbe)
        } else {
          this.$router.push({ 
            name: "Probes",
            query: this.tenant.addSchemaParam()
          });
        }
        
      } catch (error) {

        this.fieldErrors = {}; 
        const items = this.requiredprobeFields.concat(this.optionalFields);
        const errData = error?.response?.data;

        items.forEach(element => {
          const fieldError = errData?.[element.key];
          if (fieldError?.[0]) {
            this.fieldErrors[element.key] = fieldError[0]; 
          }
        });
          console.error("Error creating probe:", error); 
        }
    },
    async submitSubnet(probeId, schemaName = null){
      try{
        if (!schemaName) {
          this.error = "Malformed request. Failed to create subnet!";
          this.errorCode = "422";
          return;
        }
        const subnetPromises = this.addedSubnets.map(async (subnet) => {
          try {
            const copyOfSubnet = { ...subnet, probe: probeId };
            const newSubnet = await createSubnet(copyOfSubnet, schemaName);
            return newSubnet;
          } catch (error) {
            console.error("Error creating subnet:", error);
            return null;
          }
        }); 
        await Promise.all(subnetPromises);

      } catch (error) {
          console.error("Error in submitSubnet:", error);
      }
    },
    async submitVlan(probeId, schemaName = null){
      try {
        if (!schemaName) {
          this.error = "Malformed request. Failed to create VLAN!";
          this.errorCode = "422";
          return;
        }
        const vlanPromises = this.dropdowndata.map(async (item) => {
          try {
            const copyOfDropdownData = { ...item, probe: probeId };
            const newVlan = await createVlan(copyOfDropdownData, schemaName);
            return newVlan;
          } catch (error) {
            console.error("Error creating VLAN:", error);
            return null;
          }
        });
        await Promise.all(vlanPromises);
      } catch (error) {
        console.error("Error in submitVlans:", error);
      } 
    },
    handleSiteFocus() {
      this.clearFieldError('site')
      this.isSiteDropdownVisible = true;
      // Clear search when focusing to show all probes
      this.siteSearch = "";
    },
    handleSiteBlur() {
      setTimeout(() => {
        this.isSiteDropdownVisible = false;
      }, 200);
    },
    selectSite(site) {
      this.probeData.site = site.id;
      this.siteSearch = site.name;
      this.isSiteDropdownVisible = false;
      
    },
    handleAddVlan(){
      const copyof = {...this.newVLAN,
        routes: this.newVLAN.routes.map(route => ({ ...route }))
      }
      this.dropdowns.push(copyof); 
    },
    handleSaveVlan(index){
      const savedVlan = { ...this.dropdowns[index] }; 
      if (savedVlan?.name && savedVlan?.gateway 
        && savedVlan?.raw_device  && savedVlan?.ip_address) {
        savedVlan.routes = this.routeArray
        this.dropdowndata.push(savedVlan);
        this.dropdowns.splice(index, 1); 
        this.vlanSaved = true; 
        this.routeArray = [];
      }
    },
    removeVlan(index) {
      this.dropdowns.splice(index, 1);
      this.dropdowndata.splice(index,1)
      this.routeArray = [];
    },
    handleAddSubnet(){
      const copyof = {...this.newSubnet}
      this.subnets.push(copyof);
    },
    handleSaveSubnet(subnet){
      if (subnet?.ip_address){ 
        const copyof = {...subnet}
        this.addedSubnets.push(copyof);
        this.subnets.splice(subnet,1);
        this.subnetSaved = true;
      }
    },
    removeSubnet(index){
      this.subnets.splice(index,1);
      this.addedSubnets.splice(index,1);
    },
    addRoute(route){
      if (route?.to && route?.via) {
        const obj = {...route }
        this.routeArray.push(obj);
        this.routeAdded = true;
      }
    },
   removeRoute(route){
      const index = this.routeArray.indexOf(route);
      this.routeArray.splice(index,1);
    },
    addTarget(snmp_target){
      if (snmp_target !== null) {
        this.targets.push(snmp_target);
        this.targetAdded = true;
        this.probeData.snmp_targets = null;
      }
    },
    removeTarget(index){
      this.targets.splice(index,1);
    }
  },
  computed:{
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    filteredSites() {
      if (!this.siteSearch) return this.sites;
      return this.sites.filter((site) =>
        site.name.toLowerCase().includes(this.siteSearch.toLowerCase()) ||
        site.id.toString().includes(this.siteSearch)
      );
    },
    getSelectedSiteName() {
      if (!this.probeData.site) return null;
      const selectedSite = this.sites.find(p => p.name === this.probeData.site);
      return selectedSite? `${selectedSite.name} (ID: ${selectedSite.id})` : null;
    },
    user() {
      return useStore.getUser();
    },
  }
};
</script>
